/**
 * Utilidades para gestionar permisos de módulos
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:7890/api';

/**
 * Verifica si un usuario tiene permiso en un módulo específico
 * @param {number} userId - ID del usuario
 * @param {string} moduleName - Nombre del módulo
 * @returns {Promise<boolean>}
 */
export const hasModulePermission = async (userId, moduleName) => {
  try {
    const response = await axios.get(`${API_URL}/users/${userId}/module-permissions`);
    if (!response.data) return false;
    
    const permissions = Array.isArray(response.data) ? response.data : [];
    const modulePermission = permissions.find(
      p => p.moduleName === moduleName && p.isActive
    );
    
    return !!modulePermission;
  } catch (error) {
    console.error('Error verificando permisos de módulo:', error);
    return false;
  }
};

/**
 * Obtiene el rol de un usuario en un módulo específico
 * @param {number} userId - ID del usuario
 * @param {string} moduleName - Nombre del módulo
 * @returns {Promise<string|null>}
 */
export const getModuleRole = async (userId, moduleName) => {
  try {
    const response = await axios.get(`${API_URL}/users/${userId}/module-permissions`);
    if (!response.data) return null;
    
    const permissions = Array.isArray(response.data) ? response.data : [];
    const modulePermission = permissions.find(
      p => p.moduleName === moduleName && p.isActive
    );
    
    return modulePermission?.roleName || null;
  } catch (error) {
    console.error('Error obteniendo rol de módulo:', error);
    return null;
  }
};

/**
 * Obtiene todos los roles de módulo de un usuario
 * @param {number} userId - ID del usuario
 * @returns {Promise<Array>}
 */
export const getAllModuleRoles = async (userId) => {
  try {
    const response = await axios.get(`${API_URL}/users/${userId}/module-permissions`);
    if (!response.data) return [];
    
    const permissions = Array.isArray(response.data) ? response.data : [];
    return permissions.filter(p => p.isActive).map(p => ({
      module: p.moduleName,
      role: p.roleName
    }));
  } catch (error) {
    console.error('Error obteniendo roles de módulos:', error);
    return [];
  }
};

/**
 * Obtiene el rol principal del usuario (el primero encontrado o el rol global)
 * @param {Object} user - Objeto de usuario
 * @returns {Promise<string>}
 */
export const getPrimaryRole = async (user) => {
  if (!user) return 'Invitado';
  
  // Si es administrador, retornar directamente
  if (user.rol === 'administrador') {
    return 'Administrador';
  }
  
  // Intentar obtener roles de módulos
  try {
    const moduleRoles = await getAllModuleRoles(user.id);
    
    if (moduleRoles.length > 0) {
      // Retornar el primer rol de módulo encontrado
      const role = moduleRoles[0];
      return `${formatRoleName(role.role)} (${formatModuleName(role.module)})`;
    }
    
    // Si no tiene roles de módulo, retornar el rol global
    return formatRoleName(user.rol);
  } catch (error) {
    console.error('Error obteniendo rol principal:', error);
    return formatRoleName(user.rol);
  }
};

/**
 * Formatea el nombre de un rol para mostrar
 * @param {string} roleName - Nombre del rol
 * @returns {string}
 */
export const formatRoleName = (roleName) => {
  if (!roleName) return 'Usuario';
  
  const roleMap = {
    'administrador': 'Administrador',
    'usuario_registrado': 'Usuario Registrado',
    'calendarizador': 'Calendarizador',
    'posteador': 'Posteador',
    'gestor': 'Gestor',
    'validador': 'Validador',
    'editor': 'Editor',
    'historiador': 'Historiador',
    'lector': 'Lector',
    'estudiante': 'Estudiante'
  };
  
  return roleMap[roleName] || roleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Formatea el nombre de un módulo para mostrar
 * @param {string} moduleName - Nombre del módulo
 * @returns {string}
 */
export const formatModuleName = (moduleName) => {
  if (!moduleName) return '';
  
  const moduleMap = {
    'calendario': 'Calendario',
    'posts': 'Posts',
    'historicidad': 'Historicidad',
    'mitzvot': 'Mandamientos',
    'biblia': 'Biblia'
  };
  
  return moduleMap[moduleName] || moduleName.replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Determina si un usuario debe usar almacenamiento local o backend
 * @param {Object} user - Objeto de usuario
 * @param {string} moduleName - Nombre del módulo
 * @returns {Promise<boolean>} - true si debe usar backend, false si debe usar local
 */
export const shouldUseBackend = async (user, moduleName) => {
  if (!user) return false;
  
  // Administradores siempre usan backend
  if (user.rol === 'administrador') return true;
  
  // Verificar si tiene permiso de módulo
  const hasPermission = await hasModulePermission(user.id, moduleName);
  return hasPermission;
};


